import { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Crown, Gavel, SkipForward, DollarSign, Search, Eye } from "lucide-react";
import { playTick } from "@/lib/sound";

const tickDiffSec = (endsAt, serverNow) => {
  if (!endsAt) return 0;
  const end = new Date(endsAt).getTime();
  const now = serverNow ? new Date(serverNow).getTime() : Date.now();
  return Math.max(0, Math.floor((end - now) / 1000));
};

export default function AuctionRoom({ state, me, send, lastEvent }) {
  const { draft, participants, players } = state;
  const lot = draft.current_lot;
  const nominationDeadline = !lot ? draft.nomination_deadline : null;
  const [nomSecsLeft, setNomSecsLeft] = useState(0);

  // Local timer
  const [secsLeft, setSecsLeft] = useState(0);
  const offsetRef = useRef(0);

  useEffect(() => {
    if (state.server_time) {
      offsetRef.current = Date.now() - new Date(state.server_time).getTime();
    }
  }, [state.server_time]);

  useEffect(() => {
    if (!lot?.ends_at) {
      setSecsLeft(0);
      return;
    }
    let lastTick = -1;
    const tick = () => {
      const adjustedNow = Date.now() - offsetRef.current;
      const end = new Date(lot.ends_at).getTime();
      const next = Math.max(0, Math.ceil((end - adjustedNow) / 1000));
      setSecsLeft(next);
      // Audible tick on each second when <= 5s remaining
      if (next !== lastTick && next > 0 && next <= 5) {
        playTick();
      }
      lastTick = next;
    };
    tick();
    const id = setInterval(tick, 250);
    return () => clearInterval(id);
  }, [lot?.ends_at]);

  // Bid pulse animation trigger
  const [bidPulseKey, setBidPulseKey] = useState(0);
  useEffect(() => {
    if (lastEvent?.event === "bid_placed" || lastEvent?.event === "lot_started") {
      setBidPulseKey((k) => k + 1);
    }
  }, [lastEvent]);

  useEffect(() => {
    if (!nominationDeadline) {
      setNomSecsLeft(0);
      return;
    }
    const tick = () => {
      const adjustedNow = Date.now() - offsetRef.current;
      const end = new Date(nominationDeadline).getTime();
      setNomSecsLeft(Math.max(0, Math.ceil((end - adjustedNow) / 1000)));
    };
    tick();
    const id = setInterval(tick, 500);
    return () => clearInterval(id);
  }, [nominationDeadline]);

  const available = players.filter((p) => p.status === "available");
  const amSpectator = !!me?.is_spectator;
  const isMyTurnToNominate = !amSpectator && draft.current_nominator_id === me?.id && !lot;
  const isCurrentHighBidder = lot?.current_bidder_id === me?.id;
  const myMaxBid = useMemo(() => {
    if (!me) return 0;
    const slots = draft.roster_size - me.roster.length;
    if (slots <= 0) return 0;
    return me.budget_remaining - (slots - 1);
  }, [me, draft.roster_size]);

  return (
    <div
      className={`flex-1 grid lg:grid-cols-[1fr_360px] gap-4 lg:gap-6 px-4 sm:px-6 py-4 sm:py-6 ${amSpectator ? "pb-10" : "pb-32 lg:pb-6"} max-w-[1500px] w-full mx-auto`}
    >
      {/* Main column */}
      <section className="space-y-4">
        {amSpectator && (
          <div
            className="bg-pbo-gold/10 border border-pbo-gold/40 rounded-xl px-4 py-2.5 text-sm text-pbo-green-soft flex items-center gap-2"
            data-testid="spectator-banner"
          >
            <Eye className="w-4 h-4 text-pbo-gold" />
            <span>
              You&apos;re <strong>watching</strong> this draft. Cheer, chat, and trash-talk — bidding is left to the drafters.
            </span>
          </div>
        )}
        {/* Stage */}
        <StageCard
          lot={lot}
          secsLeft={secsLeft}
          bidPulseKey={bidPulseKey}
          totalSecs={draft.nomination_seconds}
          nominator={participants.find((p) => p.id === draft.current_nominator_id)}
          isMyTurn={isMyTurnToNominate}
          nomSecsLeft={nomSecsLeft}
          nomWindow={draft.nomination_window_seconds || 0}
        />

        {/* Nominate panel (mobile-friendly inline; desktop also shows it here) */}
        {isMyTurnToNominate && (
          <NominatePanel
            available={available}
            myMaxBid={myMaxBid}
            onNominate={(player_id, opening_bid) =>
              send({ type: "nominate", player_id, opening_bid })
            }
            onPass={() => send({ type: "pass_nomination" })}
          />
        )}

        {/* Mobile-only "your bar" — always-visible compact stats */}
        {me && !amSpectator && (
          <div
            className="lg:hidden bg-pbo-green text-pbo-cream rounded-xl p-3 flex items-center gap-3 text-xs"
            data-testid="my-mobile-bar"
          >
            <div className="font-semibold flex-1 truncate">{me.username}</div>
            <Stat label="Budget" val={`$${me.budget_remaining}`} />
            <Stat label="Roster" val={`${me.roster.length}/${draft.roster_size}`} />
            <Stat label="Max" val={`$${myMaxBid}`} />
          </div>
        )}

        {/* Tabs */}
        <Tabs defaultValue="teams" className="bg-white border border-pbo-border rounded-2xl p-4">
          <TabsList className="bg-pbo-paper">
            <TabsTrigger value="teams" data-testid="tab-teams">Teams</TabsTrigger>
            <TabsTrigger value="field" data-testid="tab-field">Field</TabsTrigger>
            <TabsTrigger value="activity" data-testid="tab-activity">Activity</TabsTrigger>
          </TabsList>
          <TabsContent value="teams" className="mt-4">
            <TeamsGrid draft={draft} participants={participants} meId={me?.id} />
          </TabsContent>
          <TabsContent value="field" className="mt-4">
            <FieldList players={players} />
          </TabsContent>
          <TabsContent value="activity" className="mt-4">
            <ActivityFeed activity={draft.activity || []} />
          </TabsContent>
        </Tabs>
      </section>

      {/* Right rail (desktop only) */}
      <aside className="hidden lg:block space-y-4">
        {amSpectator ? (
          <SpectatorCard me={me} />
        ) : (
          <MeCard me={me} draft={draft} maxBid={myMaxBid} />
        )}
        <ActivityFeed activity={draft.activity || []} compact />
      </aside>

      {/* Bid console – drafters only */}
      {!amSpectator && (
        <BidConsole
          lot={lot}
          secsLeft={secsLeft}
          me={me}
          myMaxBid={myMaxBid}
          isHighBidder={isCurrentHighBidder}
          onBid={(amount) => send({ type: "bid", amount })}
        />
      )}
    </div>
  );
}

const StageCard = ({ lot, secsLeft, bidPulseKey, totalSecs, nominator, isMyTurn, nomSecsLeft = 0, nomWindow = 0 }) => {
  // Mobile defaults to condensed; user can tap to expand. Desktop is always expanded.
  const [forceExpanded, setForceExpanded] = useState(false);
  const isDesktop = typeof window !== "undefined"
    ? window.matchMedia("(min-width: 1024px)").matches
    : true;
  const compact = !isDesktop && !forceExpanded;

  if (!lot) {
    const showNomCountdown = nomWindow > 0 && nomSecsLeft > 0;
    const urgentNom = showNomCountdown && nomSecsLeft <= 5;
    return (
      <div className="bg-pbo-green text-pbo-cream rounded-3xl p-6 sm:p-8 relative overflow-hidden">
        <div className="absolute inset-0 pbo-dotted opacity-30" />
        <div className="relative">
          <div className="text-[10px] uppercase tracking-[0.3em] text-pbo-gold-soft font-semibold">
            Up next
          </div>
          {isMyTurn ? (
            <h2 className="font-serif text-2xl sm:text-4xl mt-2 leading-tight">
              You&apos;re on the clock — nominate a golfer.
            </h2>
          ) : (
            <h2 className="font-serif text-2xl sm:text-4xl mt-2 leading-tight">
              Waiting on{" "}
              <span className="text-pbo-gold">{nominator?.username || "…"}</span> to nominate
            </h2>
          )}
          {showNomCountdown && (
            <div className="mt-4 inline-flex items-center gap-2 bg-pbo-green-deep border border-pbo-gold/30 rounded-full px-4 py-1.5">
              <span className="text-[10px] uppercase tracking-[0.18em] text-pbo-gold-soft font-semibold">
                Auto-pick in
              </span>
              <span
                className={`font-mono font-bold ${urgentNom ? "timer-urgent" : "text-pbo-cream"}`}
                data-testid="nomination-countdown"
              >
                {nomSecsLeft}s
              </span>
            </div>
          )}
        </div>
      </div>
    );
  }

  const pct = totalSecs ? Math.min(100, (secsLeft / totalSecs) * 100) : 0;
  const urgent = secsLeft <= 5;

  // ── Condensed mobile layout: hero name + bid pulse + timer in one compact row ──
  if (compact) {
    return (
      <button
        type="button"
        onClick={() => setForceExpanded(true)}
        className="w-full text-left bg-pbo-green text-pbo-cream rounded-2xl p-4 relative overflow-hidden active:scale-[0.99] transition-transform"
        data-testid="auction-stage"
        aria-label="Expand auction details"
      >
        <div className="absolute inset-0 pbo-stripe opacity-50" />
        <div className="relative flex items-center gap-3">
          <div className="flex-1 min-w-0">
            <div className="text-[9px] uppercase tracking-[0.22em] text-pbo-gold-soft font-semibold">
              On the block · {lot.nominator_name}
            </div>
            <h2 className="font-serif text-2xl mt-0.5 leading-tight truncate">
              {lot.player_name}
            </h2>
            <div className="flex items-baseline gap-1.5 mt-1">
              <span
                key={bidPulseKey}
                className="font-mono text-2xl font-semibold text-pbo-gold pulse-bid"
                data-testid="current-bid-amount"
              >
                ${lot.current_bid}
              </span>
              <span className="text-xs text-pbo-cream/70 truncate">by {lot.current_bidder_name}</span>
            </div>
          </div>
          <div className="text-right shrink-0">
            <div
              className={`font-mono font-bold text-4xl leading-none ${urgent ? "timer-urgent" : "text-pbo-cream"}`}
              data-testid="lot-timer"
            >
              {secsLeft}s
            </div>
            <div className="w-14 h-1 rounded-full bg-pbo-green-deep mt-1.5 overflow-hidden ml-auto">
              <div
                className={`h-full ${urgent ? "bg-red-400" : "bg-pbo-gold"} transition-all duration-300`}
                style={{ width: `${pct}%` }}
              />
            </div>
            <div className="text-[9px] uppercase tracking-wider text-pbo-gold-soft/70 mt-1">
              tap to expand
            </div>
          </div>
        </div>
      </button>
    );
  }

  // ── Full layout (desktop, or mobile after tap-to-expand) ──
  return (
    <div
      className="bg-pbo-green text-pbo-cream rounded-3xl p-6 sm:p-10 relative overflow-hidden"
      data-testid="auction-stage"
    >
      <div className="absolute inset-0 pbo-stripe opacity-50" />
      <div className="relative grid sm:grid-cols-[1fr_auto] gap-6 items-end">
        <div>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="text-[10px] uppercase tracking-[0.3em] text-pbo-gold-soft font-semibold">
              On the block — nominated by {lot.nominator_name}
            </div>
            {!isDesktop && (
              <button
                type="button"
                onClick={() => setForceExpanded(false)}
                className="text-[10px] uppercase tracking-wider text-pbo-gold-soft hover:text-pbo-cream"
                data-testid="collapse-stage-btn"
              >
                ▴ Collapse
              </button>
            )}
          </div>
          <h2 className="font-serif text-4xl sm:text-5xl mt-2 leading-tight">
            {lot.player_name}
          </h2>
          <div className="mt-6 flex items-baseline gap-4 flex-wrap">
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-pbo-gold-soft">
                High bid
              </div>
              <div
                key={bidPulseKey}
                className="font-mono text-5xl sm:text-6xl font-semibold text-pbo-gold pulse-bid"
                data-testid="current-bid-amount"
              >
                ${lot.current_bid}
              </div>
            </div>
            <div className="text-pbo-cream/80 text-sm">
              by{" "}
              <span className="text-pbo-cream font-semibold">
                {lot.current_bidder_name}
              </span>
            </div>
          </div>
        </div>
        <div className="text-center sm:text-right">
          <div className="text-[10px] uppercase tracking-[0.3em] text-pbo-gold-soft">
            Time
          </div>
          <div
            className={`font-mono font-bold text-6xl sm:text-7xl ${urgent ? "timer-urgent" : "text-pbo-cream"}`}
            data-testid="lot-timer"
          >
            {secsLeft}s
          </div>
          <div className="w-full h-1.5 rounded-full bg-pbo-green-deep mt-3 overflow-hidden">
            <div
              className={`h-full ${urgent ? "bg-red-400" : "bg-pbo-gold"} transition-all duration-300`}
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const NominatePanel = ({ available, myMaxBid, onNominate, onPass }) => {
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);
  const [opening, setOpening] = useState(1);

  const filtered = available.filter((p) =>
    p.name.toLowerCase().includes(query.toLowerCase())
  );

  return (
    <div
      className="bg-white border-2 border-pbo-gold rounded-2xl p-5 fade-in"
      data-testid="nominate-panel"
    >
      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <div>
          <h3 className="font-serif text-pbo-green text-xl flex items-center gap-2">
            <Crown className="w-4 h-4 text-pbo-gold" /> Your nomination
          </h3>
          <p className="text-xs text-pbo-muted">
            Choose a golfer and your opening bid.
          </p>
        </div>
        <Button
          variant="outline"
          onClick={onPass}
          className="text-xs h-8 border-pbo-border text-pbo-muted"
          data-testid="pass-nomination-btn"
        >
          <SkipForward className="w-3.5 h-3.5 mr-1" />
          Pass to next
        </Button>
      </div>

      <div className="relative mb-3">
        <Search className="w-4 h-4 absolute top-3 left-3 text-pbo-muted" />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search available golfers..."
          className="pl-9 bg-pbo-paper"
          data-testid="nominate-search-input"
        />
      </div>

      <div className="max-h-44 overflow-y-auto scrollbar-thin border border-pbo-border rounded-lg divide-y divide-pbo-border/60 bg-pbo-paper/40">
        {filtered.length === 0 && (
          <div className="p-4 text-sm text-pbo-muted text-center">No matches.</div>
        )}
        {filtered.map((p) => (
          <button
            key={p.id}
            type="button"
            onClick={() => setSelected(p)}
            className={`w-full text-left px-4 py-2.5 text-sm flex items-center justify-between ${
              selected?.id === p.id
                ? "bg-pbo-green text-pbo-cream"
                : "hover:bg-pbo-paper"
            }`}
            data-testid={`select-golfer-${p.id}`}
          >
            <span>{p.name}</span>
            {selected?.id === p.id && <span className="text-pbo-gold">✓</span>}
          </button>
        ))}
      </div>

      <div className="mt-4 flex items-end gap-3 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-pbo-muted font-semibold mb-1">
            Opening bid
          </div>
          <div className="flex items-center bg-pbo-paper border border-pbo-border rounded-lg overflow-hidden">
            <span className="px-3 text-pbo-muted font-mono">$</span>
            <Input
              type="number"
              min={1}
              max={myMaxBid}
              value={opening}
              onChange={(e) => setOpening(Number(e.target.value) || 1)}
              className="border-0 bg-transparent font-mono w-24 text-lg focus-visible:ring-0"
              data-testid="opening-bid-input"
            />
          </div>
          <div className="text-[10px] text-pbo-muted mt-1">Your max: ${myMaxBid}</div>
        </div>
        <Button
          onClick={() => selected && onNominate(selected.id, opening)}
          disabled={!selected || opening < 1 || opening > myMaxBid}
          className="bg-pbo-green text-pbo-cream hover:bg-pbo-green-deep h-12 px-6 rounded-full font-semibold"
          data-testid="confirm-nominate-btn"
        >
          <Gavel className="w-4 h-4 mr-2" />
          Nominate {selected ? selected.name.split(" ").slice(-1)[0] : "golfer"} for ${opening}
        </Button>
      </div>
    </div>
  );
};

const BidConsole = ({ lot, me, myMaxBid, isHighBidder, onBid }) => {
  const [custom, setCustom] = useState("");

  if (!lot) {
    return null; // no console between lots
  }

  const cur = lot.current_bid;
  const quick = [cur + 1, cur + 5, cur + 10, cur + 25].filter((n) => n <= myMaxBid);
  const cantBid = isHighBidder || myMaxBid <= cur || me.roster.length >= 999;

  return (
    <div className="fixed bottom-0 inset-x-0 lg:relative lg:col-span-2 z-20 bg-white border-t lg:border lg:rounded-2xl border-pbo-border lg:border-pbo-border p-3 sm:p-4 shadow-[0_-10px_30px_-15px_rgba(15,77,42,0.25)] lg:shadow-none">
      <div className="max-w-[1500px] mx-auto flex flex-col gap-3 lg:flex-row lg:items-center">
        <div className="grid grid-cols-4 gap-2 flex-1">
          {quick.length === 0 && (
            <div className="col-span-full text-center text-xs text-pbo-muted py-2">
              No bid options within your budget.
            </div>
          )}
          {quick.map((n) => (
            <Button
              key={n}
              onClick={() => onBid(n)}
              disabled={cantBid}
              variant="outline"
              className="h-14 lg:h-12 border-pbo-green text-pbo-green hover:bg-pbo-green hover:text-pbo-cream font-mono font-semibold text-base lg:text-sm"
              data-testid={`quick-bid-${n}`}
            >
              ${n}
            </Button>
          ))}
        </div>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            const n = Number(custom);
            if (!Number.isFinite(n)) return;
            onBid(n);
            setCustom("");
          }}
          className="flex items-center gap-2"
        >
          <div className="flex items-center bg-pbo-paper border border-pbo-border rounded-lg overflow-hidden flex-1 lg:flex-none">
            <span className="px-2 text-pbo-muted font-mono text-sm">$</span>
            <Input
              type="number"
              min={cur + 1}
              max={myMaxBid}
              value={custom}
              onChange={(e) => setCustom(e.target.value)}
              placeholder="Custom"
              disabled={cantBid}
              className="border-0 bg-transparent font-mono w-full lg:w-24 focus-visible:ring-0 h-12 lg:h-10"
              data-testid="custom-bid-input"
            />
          </div>
          <Button
            type="submit"
            disabled={cantBid || !custom}
            className="bg-pbo-gold text-pbo-ink hover:bg-pbo-gold-soft font-semibold h-12 lg:h-10 px-6 lg:px-5 shrink-0"
            data-testid="place-bid-btn"
          >
            <DollarSign className="w-4 h-4 mr-1" />
            Bid
          </Button>
        </form>
      </div>
      {isHighBidder && (
        <div className="text-center text-[11px] text-pbo-green-soft mt-2 font-semibold">
          You&apos;re currently the high bidder.
        </div>
      )}
    </div>
  );
};

const SpectatorCard = ({ me }) => (
  <div className="bg-pbo-green text-pbo-cream rounded-2xl p-5">
    <div className="text-[10px] uppercase tracking-[0.3em] text-pbo-gold-soft font-semibold flex items-center gap-2">
      <Eye className="w-3 h-3" /> Watching
    </div>
    <div className="font-serif text-2xl mt-1">{me?.username || "Spectator"}</div>
    <p className="text-pbo-cream/70 text-xs mt-3 leading-relaxed">
      Sit back and enjoy the show. You can chat with the room any time using the
      chat button in the bottom-left.
    </p>
  </div>
);

const MeCard = ({ me, draft, maxBid }) => {
  if (!me) return null;
  return (
    <div className="bg-pbo-green text-pbo-cream rounded-2xl p-5">
      <div className="text-[10px] uppercase tracking-[0.3em] text-pbo-gold-soft font-semibold">
        Your team
      </div>
      <div className="font-serif text-2xl mt-1">{me.username}</div>
      <div className="grid grid-cols-3 gap-2 mt-4">
        <Stat label="Budget" val={`$${me.budget_remaining}`} />
        <Stat label="Roster" val={`${me.roster.length}/${draft.roster_size}`} />
        <Stat label="Max bid" val={`$${maxBid}`} />
      </div>
      <div className="mt-4 space-y-1 text-sm" data-testid="my-roster">
        {me.roster.length === 0 ? (
          <div className="text-pbo-cream/60 text-xs italic">No picks yet — bid bold.</div>
        ) : (
          me.roster.map((r) => (
            <div
              key={r.player_id}
              className="flex justify-between items-baseline border-b border-pbo-gold/15 pb-1"
            >
              <span className="truncate pr-2">{r.name}</span>
              <span className="font-mono text-pbo-gold-soft text-sm">${r.price}</span>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

const Stat = ({ label, val }) => (
  <div>
    <div className="text-[9px] uppercase tracking-[0.18em] text-pbo-gold-soft font-semibold">
      {label}
    </div>
    <div className="font-mono font-semibold text-pbo-cream">{val}</div>
  </div>
);

const TeamsGrid = ({ draft, participants, meId }) => (
  <div className="grid sm:grid-cols-2 gap-3" data-testid="teams-grid">
    {participants.filter((p) => !p.is_spectator).map((p) => {
      const isNominator = draft.current_nominator_id === p.id;
      return (
        <div
          key={p.id}
          className={`rounded-xl border p-4 ${
            isNominator
              ? "border-pbo-gold bg-pbo-gold/5"
              : "border-pbo-border bg-pbo-paper"
          }`}
        >
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2 min-w-0">
              <span
                className={`w-2 h-2 rounded-full ${p.connected ? "bg-pbo-green" : "bg-pbo-muted/40"}`}
              />
              <span className="font-semibold text-pbo-ink truncate">{p.username}</span>
              {p.is_host && <Crown className="w-3 h-3 text-pbo-gold" />}
              {meId === p.id && (
                <span className="text-[10px] uppercase tracking-wider text-pbo-green-soft">
                  you
                </span>
              )}
              {isNominator && (
                <span className="text-[10px] uppercase tracking-wider text-pbo-gold font-semibold">
                  nominating
                </span>
              )}
            </div>
            <div className="text-xs text-pbo-muted font-mono">
              {p.roster.length}/{draft.roster_size}
            </div>
          </div>
          <div className="flex items-baseline gap-3 mb-3">
            <div>
              <div className="text-[9px] uppercase tracking-wider text-pbo-muted">
                Budget
              </div>
              <div className="font-mono font-semibold text-pbo-green">
                ${p.budget_remaining}
              </div>
            </div>
          </div>
          <div className="space-y-1 text-xs">
            {p.roster.length === 0 ? (
              <div className="text-pbo-muted italic">No picks yet</div>
            ) : (
              p.roster.map((r) => (
                <div
                  key={r.player_id}
                  className="flex justify-between border-b border-pbo-border/60 pb-1"
                >
                  <span className="truncate pr-2 text-pbo-ink/85">{r.name}</span>
                  <span className="font-mono text-pbo-muted">${r.price}</span>
                </div>
              ))
            )}
          </div>
        </div>
      );
    })}
  </div>
);

const FieldList = ({ players }) => {
  const sorted = [...players].sort((a, b) => {
    if (a.status === b.status) return a.name.localeCompare(b.name);
    return a.status === "available" ? -1 : 1;
  });
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-1.5 max-h-96 overflow-y-auto scrollbar-thin pr-2">
      {sorted.map((p) => (
        <div
          key={p.id}
          className={`flex items-center justify-between py-1.5 border-b border-pbo-border/60 text-sm ${
            p.status === "sold" ? "text-pbo-muted" : "text-pbo-ink"
          }`}
        >
          <span className="truncate pr-2">{p.name}</span>
          {p.status === "sold" ? (
            <span className="text-xs font-mono">
              <span className="text-pbo-green-soft">{p.sold_to_name}</span>
              <span className="text-pbo-gold ml-2">${p.sold_price}</span>
            </span>
          ) : (
            <span className="text-[10px] uppercase tracking-wider text-pbo-green font-semibold">
              available
            </span>
          )}
        </div>
      ))}
    </div>
  );
};

const ActivityFeed = ({ activity, compact = false }) => {
  const items = [...(activity || [])].reverse();
  return (
    <div
      className={`bg-white border border-pbo-border rounded-${compact ? "2xl" : "xl"} p-4 ${
        compact ? "" : ""
      }`}
      data-testid="activity-feed"
    >
      {compact && (
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-serif text-pbo-green text-base">Activity</h3>
        </div>
      )}
      <div
        className={`space-y-1.5 ${compact ? "max-h-72" : "max-h-80"} overflow-y-auto scrollbar-thin pr-1`}
      >
        {items.length === 0 && (
          <div className="text-sm text-pbo-muted italic">Quiet on the course…</div>
        )}
        {items.map((a, i) => (
          <div
            key={i}
            className={`text-sm border-b border-pbo-border/40 pb-1.5 ${
              i === 0 ? "text-pbo-ink font-medium" : "text-pbo-muted"
            }`}
          >
            {a.text}
          </div>
        ))}
      </div>
    </div>
  );
};
